import _ from "lodash";
import * as FacetUtil from "./FacetUtil";
import * as TrackingUtil from "./TrackingUtil";

import { SearchQuery, Query, SearchResultEx, SearchResultEntry } from "../datatypes/SearchResult";

export function mapToCountedIds(ids: Array<string>): { [id: string]: number } {
    let i = 0;
    return _.reduce(
        ids,
        (map, id) => {
            map[id] = i++;
            return map;
        },
        {} as { [k: string]: number }
    );
}

export const DEFAULT_ROWS = 12;
export const DEFAULT_ROWS_LIST = 24;
export const DEFAULT_FAVOURITES = 6;

export function getSearchResultRows(): number {
    return 24;
}

export const SORT_CRITERIA = {
    relevance: {
        CRITERIA: "relevance",
        I18N_KEY: "EX_SEARCH_SORT_BY_RELEVANCE_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-relevance",
        DESCRIPTION: "relevance"
    },
    "price asc": {
        CRITERIA: "price asc",
        I18N_KEY: "EX_SEARCH_SORT_BY_PRICE_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-price",
        DESCRIPTION: "price_asc"
    },
    "description asc": {
        CRITERIA: "description asc",
        I18N_KEY: "EX_SEARCH_SORT_BY_A_TO_Z_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-a-to-z",
        DESCRIPTION: "a_to_z"
    },
    description: {
        CRITERIA: "description",
        I18N_KEY: "EX_SEARCH_SORT_BY_Z_TO_A_LABEL",
        TEST_CSS_CLASS: "test-menu-item-sort-by-z-to-a",
        DESCRIPTION: "z_to_a"
    }
} as { [k: string]: { CRITERIA: string; I18N_KEY: string; TEST_CSS_CLASS: string; DESCRIPTION: string } };

export function enhanceWithOfferInfo(
    ids: Array<string>,
    resultMap: {
        [key: string]: SearchResultEntry;
    }
): Array<{ idToFetch: string; variantId: string }> {
    return ids.map(id => {
        let idToFetch;
        const r = resultMap[id];
        if (r && r.offers && r.offers.length > 0) {
            idToFetch = id + "@" + r.offers[0].id;
        } else {
            idToFetch = id;
        }
        return { idToFetch: idToFetch, variantId: id };
    });
}

// URL query => SearchQuery
export function buildQuery(query: Query, searchPurpose: string): SearchQuery {
    const facets = query.facets || "";
    const ranges = query.ranges || "";
    const options = query.options || "";
    const sorting = query.sorting;
    return {
        q: query.q || "*",
        categoryPath: constructCategoryPath(query),
        facets: FacetUtil.parseMultiFacetString(facets),
        ranges: FacetUtil.parseNumericFacetString(ranges),
        options: FacetUtil.parseBoolFacetString(options),
        profile: query.profile || undefined,
        sorting: sorting,
        searchPurpose: searchPurpose
    };
}

export function mergeSearchQueries(query: SearchQuery, additionalQuery: Partial<SearchQuery>): SearchQuery {
    return {
        q: query.q || additionalQuery.q || "",
        searchPurpose: query.searchPurpose || additionalQuery.searchPurpose,
        categoryPath: query.categoryPath || additionalQuery.categoryPath,
        facets: query.facets.concat(additionalQuery.facets || []),
        ranges: query.ranges.concat(additionalQuery.ranges || []),
        options: query.options.concat(additionalQuery.options || []),
        sorting: query.sorting || additionalQuery.sorting,
        profile: query.profile || additionalQuery.profile, // TODO(FD) this could actually be merged better, but we don't need it
        restrictionId: query.restrictionId || additionalQuery.restrictionId,
        onlySearchable: query.onlySearchable || additionalQuery.onlySearchable
    };
}

export function getSelectedSortCriteria(eventKey: string | null | undefined) {
    if (eventKey) {
        const result = SORT_CRITERIA[eventKey];
        if (result) {
            return result;
        }
    }
    return SORT_CRITERIA.relevance;
}

type SearchLocation = {
    pathname: string;
    query: Query;
    state?: {} | null | undefined;
};

// Search query => URL query
export function createLocationForSearchPages(query: SearchQuery, path: string): SearchLocation {
    const pathParts = query.categoryPath ? query.categoryPath.split("/") : [];
    const newQuery = {
        q: query.q,
        sorting: query.sorting || undefined,
        facets: FacetUtil.buildMultiFacetQueryString(query.facets),
        ranges: FacetUtil.buildNumericFacetQueryString(query.ranges),
        options: FacetUtil.buildBoolFacetQueryString(query.options)
    } as Query;
    // tiefkühl/speiseeis -> &cat1=tiefkühl&cat2=speiseeis
    _.forEach(pathParts, (part: string, index: number) => {
        (newQuery as any)["cat" + (index + 1)] = part;
    });
    return {
        pathname: path,
        query: newQuery,
        state: {} // reset pagination state
    };
}

export function createLocationForCategoryPage(query: SearchQuery): SearchLocation {
    return {
        pathname: "/category/" + (query.categoryPath || ""),
        query: {
            q: query.q,
            facets: FacetUtil.buildMultiFacetQueryString(query.facets),
            ranges: FacetUtil.buildNumericFacetQueryString(query.ranges),
            options: FacetUtil.buildBoolFacetQueryString(query.options),
            sorting: query.sorting || undefined
        },
        state: {} // reset pagination state
    };
}

export function updatePaginationState(
    stateParamName: string,
    router: {
        location: {
            query: Query;
            pathname: string;
            state: { [x: string]: any } | null | undefined;
        };
        replace: any;
    }
) {
    const numPages = _.get(router.location.state, stateParamName) || 1;
    const newState = _.assign({}, router.location.state || {});
    newState[stateParamName] = numPages + 1;
    const newLocation = _.assign({}, router.location, { state: newState });
    router.replace(newLocation);
}

export function constructCategoryPath(query: Query): string {
    const pathParts: string[] = [];
    let index = 1;
    while ((query as any)["cat" + index]) {
        pathParts.push((query as any)["cat" + index]);
        index += 1;
    }
    return pathParts.join("/");
}

export function loadMoreButtonsNeeded(
    regularResults: SearchResultEx,
    favourites?: SearchResultEx | null | undefined,
    searchRunning?: boolean
) {
    let page;
    let totalPages;
    let favouritesPage = 0;
    let favouritesTotalPages = 0;
    try {
        page = parseInt(regularResults.page as any, 10);
        totalPages = parseInt(regularResults.totalPages as any, 10);
        if (favourites) {
            favouritesPage = parseInt(favourites.page as any, 10);
            favouritesTotalPages = parseInt(favourites.totalPages as any, 10);
        }
    } catch (e) {
        TrackingUtil.trackUIError("SearchUtil", "", e);
        return null;
    }

    const regularNumLoaded = regularResults.resultIds ? regularResults.resultIds.length : 0;
    const allCurrentRegularsLoaded = regularResults.result
        ? regularResults.result.length === regularResults.amount || regularResults.result.length === regularNumLoaded
        : false;
    const showRegularLoadMoreButton = !searchRunning && allCurrentRegularsLoaded && totalPages > page;

    const numFavouritesFound = _.get(favourites, ["resultIds", "length"], 0);
    const numFavouritesLoaded = _.get(favourites, ["result", "length"], 0);
    const allCurrentFavouritesLoaded = numFavouritesLoaded >= numFavouritesFound;
    const showFavouritesLoadMoreButton =
        numFavouritesLoaded > 0 &&
        !searchRunning &&
        allCurrentFavouritesLoaded &&
        favouritesTotalPages > favouritesPage;
    return { showRegularLoadMoreButton, showFavouritesLoadMoreButton };
}
